import { Button, Stack, Typography, useTheme } from "@mui/material";
import SettingsLayout from "../components/SettingsLayout";
import SettingsSearchField from "../components/SettingsSearchField";
import strings from "global/constants/StringConstants";
import {
  debounceEventHandler,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { useEffect, useState } from "react";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import {
  fetchResources,
  fetchRoles,
  fetchRolesCount,
  fetchSearchCount,
  fetchSearchParams,
} from "../SettingsService";
import { AddUpdateRoles } from "./AddUpdateRoles";
import { initialRoleData } from "./RoleManagementHelpers";
import KeyboardBackspace from "@mui/icons-material/KeyboardBackspace";
import RolesTable, { RoleRow } from "./components/RolesTable";
import { createRoleButtonSx, pageTitleSx } from "./Roles.styles";

/** Built-in roles the backend ships with; they cannot be edited. */
const SYSTEM_ROLE_IDS = [1, 2];

/** The design lists five roles per page and offers no page-size control. */
const ROWS_PER_PAGE = 5;

const RoleManagement = () => {
  useTitle(title.ROLE_MANAGEMENT);
  const theme = useTheme();
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [resources, setResources] = useState<any>([]);
  const [buttonClick, setButtonClick] = useState<string>(strings.rolesTable);
  const [roleData, setRoleData] = useState<any>(initialRoleData);

  useEffect(() => {
    if (isTruthy(searchTerm)) {
      getSearchParamsData(searchTerm, pageNumber);
    } else {
      getRolesData();
    }
  }, [pageNumber, searchTerm]);

  const getRolesData = async () => {
    try {
      setLoading(true);
      const [roles, count, resources] = await Promise.all([
        fetchRoles(ROWS_PER_PAGE, pageNumber),
        fetchRolesCount(),
        fetchResources(),
      ]);
      setRoles(roles ?? []);
      setCount(count);
      setResources(resources);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const getSearchParamsData = async (search: string, page: number) => {
    try {
      let count: number;
      let response: any;
      setLoading(true);
      [count, response] = await Promise.all([
        fetchSearchCount(strings.roleManagement, search),
        fetchSearchParams(strings.roleManagement, page, ROWS_PER_PAGE, search),
      ]);
      setRoles(response ?? []);
      setCount(count);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleSearchParams = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.value) {
      setSearchTerm(event.target.value.trim());
      setPageNumber(1);
    } else {
      setSearchTerm("");
      setPageNumber(1);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    pageNumber: number,
  ) => {
    setPageNumber(pageNumber);
  };

  const handleEdit = (rowData: any) => {
    setButtonClick(strings.editRole);
    setRoleData(rowData);
  };

  const handleRoleCreator = () => {
    setButtonClick(strings.createRole);
    setRoleData(initialRoleData);
  };

  const editModeRolePage = () => {
    switch (buttonClick) {
      case strings.createRole:
      case strings.editRole:
        return (
          <AddUpdateRoles
            name={buttonClick}
            setButtonClick={setButtonClick}
            rowData={roleData}
            fetchRolesHandler={getRolesData}
            resources={resources}
          />
        );
      case strings.rolesTable:
      default:
        return (
          <RolesTable
            roles={roles}
            loading={loading}
            rowsPerPage={ROWS_PER_PAGE}
            page={pageNumber}
            count={count}
            isEditable={(role) => !SYSTEM_ROLE_IDS.includes(role.id ?? -1)}
            onEdit={handleEdit}
            onPageChange={handlePageChange}
          />
        );
    }
  };

  const notRolesTable = buttonClick !== strings.rolesTable;

  const titleNode = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={notRolesTable ? 0.5 : 0}
      onClick={() => notRolesTable && setButtonClick(strings.rolesTable)}
      onKeyPress={(e) => {
        if (notRolesTable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          setButtonClick(strings.rolesTable);
        }
      }}
      role={notRolesTable ? "button" : undefined}
      tabIndex={notRolesTable ? 0 : undefined}
      aria-label={notRolesTable ? "Go back to roles table" : undefined}
      sx={{
        cursor: notRolesTable ? "pointer" : "default",
        "&:focus": notRolesTable
          ? {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: "2px",
            }
          : {},
      }}
    >
      {notRolesTable && (
        <KeyboardBackspace htmlColor={theme.palette.neutral[900]} />
      )}
      <Typography component="h1" sx={pageTitleSx}>
        {buttonClick}
      </Typography>
    </Stack>
  );

  return (
    <SettingsLayout
      activeTab="Roles"
      title={titleNode}
      headerAction={
        buttonClick === strings.rolesTable ? (
          <>
            <SettingsSearchField
              id="roles_search_box"
              placeholder="Search roles"
              onChange={debounceEventHandler(
                handleSearchParams,
                strings.SEARCH_TIME_OUT,
              )}
            />
            <Button
              id="create_role_button"
              variant="contained"
              onClick={handleRoleCreator}
              disabled={loading}
              sx={createRoleButtonSx}
            >
              Create Role
            </Button>
          </>
        ) : undefined
      }
    >
      {editModeRolePage()}
    </SettingsLayout>
  );
};

export default RoleManagement;
